import React, {useEffect} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {StyledFormRoot} from './style.ts'
import {useForm, SubmitHandler} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import Modal from '@components/commons/Modal'
import Input from '@components/ui/Input'
import Spinner from '@components/ui/Spinner'
import {handleApiError} from '@utilities/helpers.ts'
import {useEventAddMember} from '@/features/events/queries/useEventAddMember.ts'

const enum ADD_MEMBER_MODEL {
    Email = 'email',
    EmailConfirmation = 'emailConfirmation'
}

const AddMemberSchema = z
    .object({
        [ADD_MEMBER_MODEL.Email]: z.string().min(1, {message: 'validation:email'}).email({
            message: 'validation:email_valid'
        }),
        [ADD_MEMBER_MODEL.EmailConfirmation]: z.string().min(1, {message: 'validation:email'}).email({
            message: 'validation:email_valid'
        })
    })
    .refine(data => data[ADD_MEMBER_MODEL.Email] === data[ADD_MEMBER_MODEL.EmailConfirmation], {
        path: [ADD_MEMBER_MODEL.EmailConfirmation],
        message: 'validation:confirm_email'
    })

type ValidationSchema = z.infer<typeof AddMemberSchema>

export interface AddMemberModalProps {
    onClose: () => void
    workspaceId?: string
    eventId?: string
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({onClose, workspaceId, eventId}) => {
    const {t} = useTranslation()
    const {mutate, isPending, isError, error} = useEventAddMember({
        onSuccess: onClose,
        workspaceId: `${workspaceId}`,
        eventId: `${eventId}`
    })

    const {
        register,
        handleSubmit,
        setError,
        formState: {errors, isValid, isDirty}
    } = useForm<ValidationSchema>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: zodResolver(AddMemberSchema)
    })

    useEffect(() => {
        if (isError && error instanceof Error) {
            handleApiError({setError, error})
        }
    }, [isError])

    const onSubmit: SubmitHandler<ValidationSchema> = values => {
        mutate(values)
    }

    return (
        <Modal
            overlay
            title={t('event_ingestion:add_member_modal:title')}
            onClose={onClose}
            cancelButtonCallback={onClose}
            isSubmitButtonDisabled={!isValid || !isDirty}
            submitButtonCallback={handleSubmit(onSubmit)}
            submitButtonText={'event_ingestion:add_member_modal:add_member'}
        >
            {isPending && <Spinner size={48} overlay={false} />}
            <StyledFormRoot onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <Trans
                        t={t}
                        i18nKey="event_ingestion:add_member_modal:description"
                        components={{
                            span: <span />
                        }}
                    />
                </div>
                <Input
                    {...register(ADD_MEMBER_MODEL.Email)}
                    label={`${t('common:email')}*`}
                    errorMessage={`${t(errors.email?.message || '')}`}
                />
                <Input
                    {...register(ADD_MEMBER_MODEL.EmailConfirmation)}
                    label={`${t('common:confirm_email')}*`}
                    errorMessage={`${t(errors.emailConfirmation?.message || '')}`}
                />
            </StyledFormRoot>
        </Modal>
    )
}

export default AddMemberModal

AddMemberModal.displayName = 'AddMemberModal'
